import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * Forward Deployed AI Engineer — the sixth open role, and the first one whose
 * Excellence opening existed before its page did.
 *
 * The other five went the other way: the AI editor wrote the pages on
 * 2026-09-07 and the openings were seeded to match. This one arrived from
 * Yasmin on 2026-09-10 as a .docx with 29 sourced CVs attached, went into
 * Excellence the same afternoon (`hr.job_openings.slug =
 * 'forward-deployed-ai-engineer'`, published, 29 candidates on its pipeline),
 * and sat there unreachable from the internet until Kagan said *"sayfasını da
 * yaz, halka açık olsun"*. **The slug is the join** — this file's `path` and
 * the `apply` section's `opening` must both equal it, or the form at the foot
 * of the page posts to a role that does not exist.
 *
 * The copy is Yasmin's ad, section for section, with two deliberate departures:
 *
 *   * **The mission paragraph is the site's, not the ad's.** The ad says
 *     "#transformbetter"; the five sibling role pages and the footer all say
 *     "#ScaleValue". One page using the other hashtag would read as a mistake
 *     rather than as a choice, so this page matches its neighbours and the
 *     question of which hashtag the company actually uses is left to
 *     Marketing rather than settled here by accident.
 *   * **The four "would be a plus" lines arrived glued into one paragraph** in
 *     the .docx ("…Anthropic's Claude.AI patterns: Agentic AI…"), a Word list
 *     that lost its bullets somewhere. They are four items here.
 *
 * Everything else — the seven things you'd work on, the eleven fit lines, the
 * eight offers — is the ad as written.
 */
export const route: RouteDef = {
  id: 'careersForwardDeployedAiEngineer',
  path: 'become-a-digitopian/forward-deployed-ai-engineer',
  priority: 0.6,
}

export const careersForwardDeployedAiEngineer: WrittenPage = {
  navLabel: 'Forward Deployed AI Engineer',
  title: 'Forward Deployed AI Engineer',
  description:
    'Open role at Digitopia: Forward Deployed AI Engineer, hybrid in Istanbul. A hands-on builder, curious consultant and structured problem solver in one — take an ambiguous client problem from the first conversation to a production AI solution.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Careers',
      headline: 'Forward Deployed AI Engineer',
      sub: 'Location: Hybrid (Istanbul) — Full-time',
      body: [
        'A hands-on builder, curious consultant and structured problem solver in one. You will work closely with customers, navigate ambiguous business challenges, and turn them into secure, scalable AI solutions that move from the first conversation to production.',
      ],
      ctas: [{ label: 'Apply now', href: '#apply' }],
    },
    {
      kind: 'prose',
      heading: 'We’re on a mission',
      paragraphs: [
        'Digitopia is reinventing consulting. We help ambitious organizations #ScaleValue. As creators of the Impact Platform and maturity frameworks, we help companies understand where they stand, decide what matters next, and turn their digital and AI ambitions into measurable progress.',
        'Now, we are taking the next step in our AI-first journey: helping organizations turn AI ambition into practical solutions, measurable business value, and lasting transformation.',
        'To make that happen, we are looking for a Forward Deployed AI Engineer.',
      ],
    },
    {
      kind: 'list',
      heading: 'What success looks like',
      intro:
        'You will bridge the gap between business ambition and technical execution. Success means:',
      items: [
        'Turning high-value business problems into working AI solutions with measurable impact.',
        'Owning delivery from discovery and technical scoping through prototyping, deployment and adoption.',
        'Building trust with business leaders, end users and technical teams.',
        'Moving fast without losing sight of quality, security, governance or maintainability.',
      ],
    },
    {
      kind: 'features',
      variant: 'rows',
      heading: 'What you’d work on',
      items: [
        {
          title: 'Client needs into technical solutions',
          body: 'Work with Digitopia’s consulting, product and engineering teams to understand business challenges and translate them into clear AI use cases and technical requirements.',
        },
        {
          title: 'Field insights into product improvements',
          body: 'Share recurring client needs with product and engineering teams to shape reusable solutions.',
        },
        {
          title: 'Building AI solutions',
          body: 'Prototype, develop, test and support the implementation of generative and agentic AI solutions.',
        },
        {
          title: 'Modern technologies',
          body: 'Build with AWS services, Anthropic models, and other technologies selected according to each project’s needs.',
        },
        {
          title: 'Reliability',
          body: 'Contribute to agentic applications, including evaluations, guardrails, and appropriate reliability and context-management practices.',
        },
        {
          title: 'Enterprise systems',
          body: 'Integrate solutions with APIs, data sources, business applications and platforms such as SAP.',
        },
        {
          title: 'Idea to implementation',
          body: 'Collaborate with internal teams, contribute to technical planning, identify potential risks, and help turn prototypes into practical solutions.',
        },
      ],
    },
    {
      kind: 'list',
      heading: 'What makes you a great fit',
      items: [
        'You bring relevant experience across software engineering, AI/ML, data or solutions engineering, technical consulting, or a related field.',
        'Strong new graduates with meaningful internships, academic work, open-source contributions or personal projects are encouraged to apply.',
        'You can build: you are comfortable coding in Python and ideally at least one additional language from our stack, such as Kotlin or Java for backend services, or TypeScript/JavaScript for frontend development.',
        'You understand the fundamentals of APIs, SQL, data pipelines, software architecture, version control and cloud environments.',
        'You have built or meaningfully contributed to an AI, ML, LLM or generative AI application.',
        'Previous experience building similar solutions is a strong advantage. Please include links to relevant projects, a portfolio, GitHub profile, case studies or demos where available.',
        'You are curious about how businesses operate and can translate a real-world problem into a technical solution.',
        'You communicate clearly with both technical and non-technical stakeholders.',
        'You are comfortable working with evolving requirements and making progress without having every answer upfront.',
        'You take ownership, ask thoughtful questions, and follow through.',
        'You are fluent in English. An additional language is a plus.',
      ],
    },
    {
      kind: 'list',
      heading: 'Experience with any of these would be a plus',
      items: [
        'Cloud and AI platforms: AWS, AWS Bedrock, and Anthropic’s Claude.',
        'AI patterns: agentic AI, RAG, vector databases, prompt caching, evaluations and guardrails.',
        'Backend and integration: Kotlin, Java, Spring, gRPC, OpenAPI/Swagger, PostgreSQL, SAP or SAP BTP, and enterprise system integration.',
        'Data and infrastructure: Databricks, Snowflake, Docker, Kubernetes, Terraform, and CI/CD.',
      ],
      body: 'You do not need to match every keyword. We care more about how quickly you learn, how effectively you apply new knowledge, what you can build, and how seriously you take ownership.',
    },
    {
      kind: 'features',
      variant: 'rows',
      heading: 'What’s in it for you',
      items: [
        {
          title: 'Impact from day one',
          body: 'Work on real customer problems and see the solutions you build create measurable value.',
        },
        {
          title: 'A rare career path',
          body: 'Grow at the intersection of AI engineering, consulting, product thinking and customer delivery.',
        },
        {
          title: 'Modern AI exposure',
          body: 'Work with AWS Bedrock, Anthropic’s Claude, enterprise platforms, and the latest developments in generative and agentic AI.',
        },
        {
          title: 'Continuous growth',
          body: 'Access mentorship, courses, experimentation time and internal knowledge-sharing.',
        },
        {
          title: 'Real ownership',
          body: 'Take responsibility for meaningful projects.',
        },
        {
          title: 'Balanced flexibility',
          body: 'Enjoy hybrid working, flexible hours and generous paid time off.',
        },
        {
          title: 'Supportive culture',
          body: 'Join a team that values authenticity, curiosity, bold ideas and learning through action.',
        },
        {
          title: 'Long-term rewards',
          body: 'Receive a highly competitive compensation package with stock options tied to your lasting impact.',
        },
      ],
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Ready to turn real-world problems into AI systems people actually use?',
      sub: 'Apply now. We would love to meet you if you are ready to build, learn and own the outcome.',
      cta: { label: 'Apply now', href: '#apply' },
    },
    {
      kind: 'apply',
      opening: 'forward-deployed-ai-engineer',
      heading: 'Apply for Forward Deployed AI Engineer',
      sub: 'Hybrid (Istanbul), full-time. Tell us who you are and attach your CV; we read every application and reply either way.',
    },
  ],
}
